import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal de registro de contato — abre APÓS o clique em "Ligação" ou "WhatsApp".
 * Fluxo: user clica → modal → escreve o que foi tratado (obrigatório) → salva.
 *
 * Props:
 *  - open, onClose, onSaved
 *  - pessoa: { id, nome, funcao, telefone, whatsapp }
 *  - canal: "ligacao" | "whatsapp"
 *  - municipioId: string
 *  - cursoId?: string (contexto do funil, opcional)
 */
export default function ContatoRegistrarModal({
  open, onClose, onSaved, pessoa, canal, municipioId, cursoId,
}) {
  const [resultado, setResultado] = useState("atendeu");
  const [obs, setObs] = useState("");
  const [dataRetorno, setDataRetorno] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setResultado("atendeu");
      setObs("");
      setDataRetorno("");
    }
  }, [open]);

  if (!pessoa) return null;

  const isLig = canal === "ligacao";
  const Ico = isLig ? Phone : MessageCircle;
  const label = isLig ? "Ligação" : "WhatsApp";
  const styles = isLig
    ? { title: "text-blue-700", box: "bg-blue-50 border-blue-200", btn: "bg-blue-600 hover:bg-blue-700" }
    : { title: "text-emerald-700", box: "bg-emerald-50 border-emerald-200", btn: "bg-emerald-600 hover:bg-emerald-700" };

  const RESULTADOS_LIG = [
    { v: "atendeu", l: "Atendeu — conversamos" },
    { v: "nao_atendeu", l: "Não atendeu / caixa postal" },
    { v: "agendou_retorno", l: "Agendou retorno" },
    { v: "recusou", l: "Recusou / não quer falar" },
  ];
  const RESULTADOS_WA = [
    { v: "respondeu", l: "Respondeu — conversamos" },
    { v: "visualizou", l: "Visualizou, mas não respondeu" },
    { v: "nao_visualizou", l: "Ainda não visualizou" },
    { v: "agendou_retorno", l: "Agendou retorno" },
    { v: "recusou", l: "Recusou / não quer falar" },
  ];
  const resultados = isLig ? RESULTADOS_LIG : RESULTADOS_WA;

  const salvar = async () => {
    if (!obs.trim() || obs.trim().length < 3) {
      toast.error("Descreva brevemente o que foi tratado");
      return;
    }
    setSaving(true);
    try {
      const obsFinal = `[${resultados.find((r) => r.v === resultado)?.l || resultado}] ${obs.trim()}`;
      const payload = {
        pessoa_id: pessoa.id,
        municipio_id: municipioId,
        canal,
        observacao: obsFinal,
        resultado,  // Iter 63 · meta diária só conta prospecção efetiva
      };
      if (cursoId) payload.curso_id = cursoId;
      if (dataRetorno) payload.data_retorno = dataRetorno;
      await api.post("/contatos", payload);
      toast.success(`${label} registrada ⚡`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar");
    } finally {
      setSaving(false);
    }
  };

  const telefone = isLig ? (pessoa.telefone || pessoa.whatsapp) : (pessoa.whatsapp || pessoa.telefone);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="modal-contato-registrar" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${styles.title}`}>
            <Ico className="w-5 h-5" /> Registrar {label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className={`rounded-xl p-3 ${styles.box} border`}>
            <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">Contato com</div>
            <div className="font-display text-lg font-bold text-slate-900">{pessoa.nome}</div>
            <div className="text-sm text-slate-600">{pessoa.funcao}{telefone ? ` · ${telefone}` : ""}</div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">
              Como foi o {isLig ? "atendimento" : "contato"}?
            </Label>
            <Select value={resultado} onValueChange={setResultado}>
              <SelectTrigger data-testid="contato-resultado"><SelectValue /></SelectTrigger>
              <SelectContent>
                {resultados.map((r) => (
                  <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">
              O que foi conversado / próxima ação *
            </Label>
            <Textarea
              rows={3}
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              placeholder="Ex.: Falei sobre o curso de LRF. Pediu proposta com desconto para 4 vereadores. Vai levar na Câmara na terça."
              data-testid="contato-obs"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">
              Agendar retorno (opcional)
            </Label>
            <Input
              type="date"
              value={dataRetorno}
              onChange={(e) => setDataRetorno(e.target.value)}
              data-testid="contato-retorno"
            />
            <div className="text-[11px] text-slate-400 mt-1">
              Se agendar, aparece na sua Agenda no dia certo.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            onClick={salvar}
            disabled={saving}
            className={styles.btn}
            data-testid="contato-salvar"
          >
            {saving ? "Salvando..." : `Registrar ${label}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
